import { TranslateModule } from '@ngx-translate/core';
import { Component, inject, input, output } from '@angular/core';

import { ToasterService } from '../../services/toaster.service';
import { ButtonComponent } from '../button/button.component';
import { JsonDisplayComponent } from '../json-display/json-display.component';
import { ModalBaseComponent } from '../modal/modal-base/modal-base.component';

@Component({
  selector: 'lib-did-document-modal',
  imports: [TranslateModule, ModalBaseComponent, ButtonComponent, JsonDisplayComponent],
  templateUrl: './did-document-modal.component.html',
  styleUrl: './did-document-modal.component.scss',
})
export class DidDocumentModalComponent {
  private toasterService = inject(ToasterService);

  isOpen = input(false);
  didDocument = input<unknown>(null);

  handleClose = output<void>();

  onClose() {
    this.handleClose.emit();
  }

  onCopy() {
    const jsonData = this.didDocument();
    if (!jsonData) {
      this.toasterService.error({ message: 'No DID document available to copy' });
      return;
    }

    const jsonText = JSON.stringify(jsonData, null, 2);
    navigator.clipboard
      .writeText(jsonText)
      .then(() => {
        this.toasterService.info({ message: 'DID document copied to clipboard' });
      })
      .catch((error) => {
        console.error('Failed to copy to clipboard:', error);
        this.toasterService.error({ message: 'Failed to copy to clipboard' });
      });
  }

  onDownload() {
    const jsonData = this.didDocument();
    if (!jsonData) {
      this.toasterService.error({ message: 'No DID document available to download' });
      return;
    }

    try {
      const jsonText = JSON.stringify(jsonData, null, 2);
      const blob = new Blob([jsonText], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Create a descriptive filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const id = (jsonData as { id?: unknown }).id;
      const did = id ? `-${String(id).split(':').pop()?.slice(-8)}` : '';
      link.download = `did-document${did}-${timestamp}.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.toasterService.success({ message: 'DID document downloaded successfully' });
    } catch (error) {
      console.error('Failed to download DID document:', error);
      this.toasterService.error({ message: 'Failed to download DID document' });
    }
  }
}
